import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { Chapter } from '@data';
import { useListSprints } from '@data/hooks/sprints';
import { runViewTransition } from '@app/components/core/viewTransition';
import { dayKey } from '@app/utils/helpers/date-utils';
import { readBudget, type VersionTeam } from './budget';
import ForecastTab, { type CostReading } from './ForecastTab';
import { useActiveMembers } from './activeMembers';

/** What `?view=` opens; `budgeted` and `billed` are the tab names links made before the forecast still carry. */
const READINGS: Record<string, CostReading> = {
  projected: 'projected',
  budgeted: 'projected',
  actual: 'actual',
  billed: 'actual',
};

/** The year's money as it is heading, with the projection or the actuals alone one pick away. */
const CostsTab = ({
  teams,
  chapters,
  canEdit,
  productName,
}: {
  teams: VersionTeam[];
  chapters: Chapter[];
  canEdit: boolean;
  productName: string;
}): JSX.Element => {
  const { productId = '' } = useParams<{ productId: string }>();
  const [params, setParams] = useSearchParams();
  // Held here as well as in the address: a fade needs the change to land at once, and the router's does not.
  const [reading, setReading] = useState<CostReading>(() => READINGS[params.get('view') ?? ''] ?? 'forecast');

  // On the product now: hours reported lately, or a budget for the sprint running today.
  const reported = useActiveMembers(productId, undefined);
  const { data: sprintList } = useListSprints({ limit: 400 });
  const activeIds = useMemo(() => {
    if (reported.isLoading || reported.isError) return undefined;
    const today = dayKey(new Date());
    const current = (sprintList?.items ?? []).find(
      sprint => sprint.startDate.slice(0, 10) <= today && sprint.endDate.slice(0, 10) >= today,
    );
    const budgeted = Object.entries((current && readBudget(productId, current.id)?.hours) ?? {})
      .filter(([, hours]) => hours > 0)
      .map(([id]) => id);
    return new Set([...reported.active, ...budgeted]);
  }, [reported.isLoading, reported.isError, reported.active, sprintList, productId]);

  const read = (next: CostReading): void => {
    runViewTransition('fade', () => setReading(next));
    setParams(
      current => {
        const query = new URLSearchParams(current);
        if (next === 'forecast') query.delete('view');
        else query.set('view', next);
        return query;
      },
      { replace: true },
    );
  };

  return (
    <ForecastTab
      teams={teams}
      chapters={chapters}
      canEdit={canEdit}
      productName={productName}
      reading={reading}
      onRead={read}
      activeIds={activeIds}
      opensOn={params.get('sprint') ?? undefined}
    />
  );
};

export default CostsTab;
